/**
 * useActorNames — Batch actor ID → display name resolution.
 *
 * Uses the generated useListActorsQuery (Apollo) to fetch actors once,
 * then builds a lookup map. Apollo cache ensures a single network request
 * shared across all components in the render tree.
 *
 * Actor ID format: sequential `{tenant_num}_{user_num}` (e.g., "17_2").
 * Returns the actor's `name` field, or the raw ID as fallback.
 *
 * DUMB FRONTEND: All data from backend via GraphQL. No hardcoded names.
 */

import { useMemo } from 'react';
import { useListActorsQuery } from '@/generated/graphql';

export interface ActorNameResolver {
  /** Resolve an actor ID to its display name (falls back to raw ID) */
  resolveActorName: (actorId: string | null | undefined) => string;
  /** Whether the actor list is still loading */
  loading: boolean;
  /** The underlying actor map (id → name) */
  actorMap: Map<string, string>;
}

/**
 * Hook: resolve actor IDs to human-readable names.
 *
 * Fetches up to 500 actors (adjustable). The Apollo cache
 * deduplicates across multiple hook instances on the same page.
 */
export function useActorNames(limit = 500): ActorNameResolver {
  const { data, loading, error } = useListActorsQuery({
    variables: { limit },
    // Use cache-first since actor list is relatively stable
    fetchPolicy: 'cache-first',
    // Don't throw on network errors - gracefully degrade to showing raw IDs
    errorPolicy: 'ignore',
  });

  // Log errors but don't crash - return empty map and show raw IDs
  if (error) {
    console.warn('[useActorNames] Query error, falling back to raw IDs:', error.message);
  }

  const actorMap = useMemo(() => {
    const map = new Map<string, string>();
    if (data?.listActors?.items) {
      for (const actor of data.listActors.items) {
        map.set(actor.id, actor.name);
      }
    }
    return map;
  }, [data]);

  const resolveActorName = useMemo(() => {
    return (actorId: string | null | undefined): string => {
      if (!actorId) return '—';
      return actorMap.get(actorId) ?? actorId;
    };
  }, [actorMap]);

  return { resolveActorName, loading, actorMap };
}
